import { Button, Drawer, formatTime, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { issueFrontDeskCode, patchMember, type Member } from "../../api/members";
import { Field, FormError, errorMessage, useArea } from "./common";
import { t } from "./strings";
import s from "./members.module.css";

/** One-time sign-in code for a member without their phone. Needs the member's real number, so it is offered only when the phone is visible. */
export function LoginCodeDrawer({ member, onClose }: { member: Member; onClose: () => void }) {
  const { client, ctx } = useArea();
  const D = t.drawer.code;
  const issue = useMutation({ mutationFn: () => issueFrontDeskCode(client, member.phone) });
  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(); }} title={D.title} description={member.name}>
      <div className={s.form}>
        <p className={s.hint}>{D.intro}</p>
        {issue.data ? (
          <>
            <div className={s.code} aria-label={D.code} data-testid="login-code">{issue.data.code}</div>
            <div className={s.good} role="status">{D.expires(formatTime(issue.data.expiresAt, ctx.timeZone))}</div>
          </>
        ) : (
          <FormError message={issue.error ? errorMessage(issue.error) : null} />
        )}
        <div className={s.headActs}>
          {issue.data ? null : <Button variant="primary" busy={issue.isPending} busyLabel={t.drawer.working} onClick={() => issue.mutate()}>{D.issue}</Button>}
          <Button onClick={onClose}>{t.drawer.close}</Button>
        </div>
      </div>
    </Drawer>
  );
}

export function EditMemberDrawer({ member, onClose }: { member: Member; onClose: () => void }) {
  const { client, ctx } = useArea();
  const qc = useQueryClient();
  const toast = useToast();
  const E = t.edit;
  const branches = ctx.me?.branches ?? [];
  const [name, setName] = useState(member.name);
  const [email, setEmail] = useState(member.email ?? "");
  const [branchId, setBranchId] = useState(member.homeBranchId ?? "");
  const [problems, setProblems] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () => {
      const patch: Parameters<typeof patchMember>[2] = {};
      if (name.trim() !== member.name) patch.name = name.trim();
      if ((email.trim() || null) !== member.email) patch.email = email.trim() || null;
      if (branchId && branchId !== member.homeBranchId) patch.homeBranchId = branchId;
      return patchMember(client, member.id, patch);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["member"] });
      void qc.invalidateQueries({ queryKey: ["members"] });
      toast.show(E.saved);
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t.add.nameRequired;
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = t.add.emailInvalid;
    setProblems(next);
    if (Object.keys(next).length === 0) save.mutate();
  };
  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(); }} title={E.title} description={E.phoneNote}>
      <form className={s.form} onSubmit={submit} noValidate>
        <Field id="ed-name" label={E.name} error={problems.name}>
          <input id="ed-name" className={s.input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field id="ed-email" label={E.email} error={problems.email}>
          <input id="ed-email" type="email" className={s.input} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="ed-branch" label={E.branch}>
          <select id="ed-branch" className={s.select} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {!member.homeBranchId ? <option value="">—</option> : null}
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <FormError message={save.error ? errorMessage(save.error) : null} />
        <div className={s.headActs}>
          <Button type="submit" variant="primary" busy={save.isPending} busyLabel={E.saving}>{E.save}</Button>
          <Button onClick={onClose}>{t.drawer.close}</Button>
        </div>
      </form>
    </Drawer>
  );
}
